import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Camera, Upload, X } from 'lucide-react';

interface TaskMediaUploadProps {
  open: boolean;
  onClose: () => void;
  onUpload: (files: File[], caption: string) => void;
}

export function TaskMediaUpload({ open, onClose, onUpload }: TaskMediaUploadProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [caption, setCaption] = useState('');

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFiles(Array.from(event.target.files));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onUpload(files, caption);
    setFiles([]);
    setCaption('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Photos or Videos</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Button
              type="button"
              variant="outline"
              className="h-32 flex flex-col items-center justify-center gap-2"
              onClick={() => document.getElementById('camera-input')?.click()}
            >
              <Camera className="h-8 w-8" />
              <span>Take Photo/Video</span>
              <Input
                id="camera-input"
                type="file"
                accept="image/*,video/*"
                capture="environment"
                className="hidden"
                onChange={handleFileSelect}
              />
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-32 flex flex-col items-center justify-center gap-2"
              onClick={() => document.getElementById('file-input')?.click()}
            >
              <Upload className="h-8 w-8" />
              <span>Upload Files</span>
              <Input
                id="file-input"
                type="file"
                accept="image/*,video/*"
                multiple
                className="hidden"
                onChange={handleFileSelect}
              />
            </Button>
          </div>

          {files.length > 0 && (
            <div className="space-y-2">
              <Label>Selected Files</Label>
              <div className="space-y-2">
                {files.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 rounded-lg border"
                  >
                    <span className="text-sm truncate">{file.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setFiles(files.filter((_, i) => i !== index))}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="caption">Caption</Label>
            <Textarea
              id="caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Add a description for the photos/videos..."
              rows={3}
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={files.length === 0}
          >
            Upload Files
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}